import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toLocalDateTimeInput } from './dateInput';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog';
import { Badge } from './components/ui/badge';
import { Textarea } from './components/ui/textarea';

const AdminLoanManagement = ({ isOpen, onClose }) => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [editForm, setEditForm] = useState({
    loan_date: '',
    due_date: '',
    return_date: '',
    status: '',
    admin_notes: ''
  });
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/loans/admin/all', {
        params: {
          limit: 200,
          status: statusFilter === 'all' ? undefined : statusFilter
        }
      });
      console.log('🔍 Loan data received:', response.data);
      setLoans(response.data);
    } catch (error) {
      console.error('Failed to fetch loans:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchLoans();
    }
  }, [isOpen, statusFilter]);

  const handleEditLoan = (loan) => {
    setEditingLoan(loan);
    setEditForm({
      loan_date: toLocalDateTimeInput(loan.loan_date),
      due_date: toLocalDateTimeInput(loan.due_date),
      return_date: toLocalDateTimeInput(loan.return_date),
      status: loan.status || '',
      admin_notes: loan.admin_notes || ''
    });
  };

  const handleUpdateLoan = async () => {
    try {
      const updateData = {};
      
      // Only include fields that have values
      if (editForm.loan_date) {
        updateData.loan_date = new Date(editForm.loan_date).toISOString();
      }
      if (editForm.due_date) {
        updateData.due_date = new Date(editForm.due_date).toISOString();
      }
      if (editForm.return_date) {
        updateData.return_date = new Date(editForm.return_date).toISOString();
      }
      if (editForm.status) {
        updateData.status = editForm.status;
      }
      if (editForm.admin_notes !== undefined) {
        updateData.admin_notes = editForm.admin_notes;
      }

      await axios.put(`/loans/${editingLoan.id}/admin-update`, updateData);
      
      setEditingLoan(null);
      fetchLoans(); // Refresh the list
      
      // Show success message
      alert('Loan updated successfully!');
    } catch (error) {
      console.error('Failed to update loan:', error);
      alert('Failed to update loan. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'returned': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'returned') return false;
    return new Date(dueDate) < new Date();
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Admin Loan Management</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Filters */}
            <div className="flex gap-4 mb-4 p-4 bg-gray-50 rounded">
              <div className="flex items-center gap-2">
                <Label htmlFor="status-filter">Status:</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Loans</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="returned">Returned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchLoans} variant="outline">
                Refresh
              </Button>
            </div>

            {/* Loans Table */}
            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-2 text-left">Borrower</th>
                        <th className="border p-2 text-left">Media</th>
                        <th className="border p-2 text-left">Loan Date</th>
                        <th className="border p-2 text-left">Due Date</th>
                        <th className="border p-2 text-left">Return Date</th>
                        <th className="border p-2 text-left">Status</th>
                        <th className="border p-2 text-left">Notes</th>
                        <th className="border p-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loans.map((loan) => (
                        <tr key={loan.id} className={isOverdue(loan.due_date, loan.status) ? 'bg-red-50' : ''}>
                          <td className="border p-2">
                            <div className="text-sm">
                              <div className="font-medium">
                                {loan.user_name && loan.user_name.trim() ? loan.user_name : 'Unknown User'}
                              </div>
                              <div className="text-gray-500">{loan.user_email || 'No email'}</div>
                            </div>
                          </td>
                          <td className="border p-2">
                            <div className="text-sm">
                              <div className="font-medium">{loan.media_title}</div>
                              <div className="text-gray-500 capitalize">{loan.media_type}</div>
                            </div>
                          </td>
                          <td className="border p-2 text-sm">{formatDate(loan.loan_date)}</td>
                          <td className="border p-2 text-sm">
                            <span className={isOverdue(loan.due_date, loan.status) ? 'text-red-600 font-medium' : ''}>
                              {formatDate(loan.due_date)}
                            </span>
                          </td>
                          <td className="border p-2 text-sm">{formatDate(loan.return_date)}</td>
                          <td className="border p-2">
                            <Badge className={getStatusColor(loan.status)}>
                              {loan.status}
                            </Badge>
                          </td>
                          <td className="border p-2 text-sm max-w-40 truncate">
                            {loan.admin_notes || '-'}
                          </td>
                          <td className="border p-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEditLoan(loan)}
                            >
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={onClose} variant="outline">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Loan Dialog */}
      <Dialog open={!!editingLoan} onOpenChange={() => setEditingLoan(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Loan - {editingLoan?.media_title}</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="loan_date">Loan Date</Label>
                <Input
                  id="loan_date"
                  type="datetime-local"
                  value={editForm.loan_date}
                  onChange={(e) => setEditForm({...editForm, loan_date: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="datetime-local"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({...editForm, due_date: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="return_date">Return Date</Label>
                <Input
                  id="return_date"
                  type="datetime-local"
                  value={editForm.return_date}
                  onChange={(e) => setEditForm({...editForm, return_date: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({...editForm, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="returned">Returned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="admin_notes">Admin Notes</Label>
              <Textarea
                id="admin_notes"
                placeholder="Add notes about this loan modification..."
                value={editForm.admin_notes}
                onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})}
                rows={3}
              />
            </div>
            
            {editingLoan && (
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <strong>Borrower:</strong> {editingLoan.user_name || 'Unknown User'} ({editingLoan.user_email || 'No email'})
                <br />
                <strong>Current Status:</strong> {editingLoan.status}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLoan(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateLoan}>
              Update Loan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminLoanManagement;